import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
//import axios from 'axios';
import TaskForm from './TaskForm';
import api from '../../services/api';
//import { toast } from 'react-toastify';
import toast from '../../utils/toast';
import 'react-toastify/dist/ReactToastify.css';
import { 
  CheckIcon, 
  XMarkIcon, 
  ClockIcon, 
  UserIcon, 
  CalendarIcon,
  DocumentIcon,
  EyeIcon,
  TrashIcon,
  PlusIcon,
  MinusIcon
} from '@heroicons/react/24/outline';
import PasteUploadArea from '../PasteUploadArea';
import { createVerificationRecord } from '../../utils/documentVerification';

const TaskList = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isVerificationModalVisible, setIsVerificationModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [verificationFields, setVerificationFields] = useState({});
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [verificationTypes, setVerificationTypes] = useState([]);
  const [filters, setFilters] = useState({
    status: null,
    client: null,
    assignedTo: null,
    dateRange: null
  });

  useEffect(() => {
    fetchTasks();
    fetchClients();
    fetchUsers();
    fetchGroups();
    fetchVerificationTypes();
  }, [filters]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tasks', { params: filters });
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/master-data');
      setClients(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await api.get('/groups');
      setGroups(response.data);
    } catch (error) {
      toast.error('Failed to fetch groups');
    }
  };

  const fetchVerificationTypes = async () => {
    try {
      const response = await api.get('/admin/verification-types');
      setVerificationTypes(response.data);
    } catch (error) {
      toast.error('Failed to fetch verification types');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      if (newStatus === 'completed') {
        const task = tasks.find(t => t._id === taskId);
        
        // Check if all verification fields are already filled
        if (task.verificationTypes && task.verificationTypes.length > 0) {
          const areAllFieldsFilled = task.verificationTypes.every(vt => {
            const verificationType = verificationTypes.find(v => v._id === vt.id);
            if (!verificationType) return true;
            
            const fieldValue = task.verificationData?.[verificationType.label];
            return fieldValue && fieldValue.trim() !== '';
          });
          
          if (areAllFieldsFilled) {
            // All fields are filled, proceed with status update directly
            await api.patch(`/tasks/${taskId}/status`, {
              status: newStatus,
              verificationData: task.verificationData
            });
            toast.success('Task completed successfully');
            fetchTasks();
            return;
          }
        }
        
        // Show verification modal if not all fields are filled
        setSelectedTask(task);
        setPendingStatusChange(newStatus);
        setVerificationFields({});
        sendCode(taskId);
        setIsVerificationModalVisible(true);
      } else {
        await api.patch(`/tasks/${taskId}/status`, { status: newStatus });
        toast.success('Task status updated successfully');
        fetchTasks();
      }
    } catch (error) {
      toast.error('Failed to update task status');
    }
  };

  const handleVerificationSubmit = async () => {
    try {
      // Verify all required fields are filled
      const task = selectedTask;
      const requiredFields = task.verificationTypes || [];
      console.log(requiredFields);
      console.log(verificationFields);  

      const missingFields = requiredFields.filter(field => {
        const verificationType = verificationTypes.find(vt => vt._id === field.id);
        return (verificationType?.type !== 'otp-email' && !verificationFields[verificationType?.label]);
      });
      console.log(missingFields);

      if (missingFields.length > 0) {
        toast.error(`Please fill all verification fields: ${missingFields.join(', ')}`);
        return;
      }

      // Submit verification and status change
      await api.patch(`/tasks/${task._id}/status`, {
        status: pendingStatusChange,
        verificationData: verificationFields
      });

      setIsVerificationModalVisible(false);
      setPendingStatusChange(null);
      setSelectedTask(null);
      setVerificationFields({});
      toast.success('Task completed successfully');
      fetchTasks();

    } catch (error) {
      toast.error('Failed to complete task verification\n' + error.response.data.message);
      console.log(error.response.data.message);
    }
  };

  const sendCode = async (taskId) => {
    try {
      await api.post(`/tasks/${taskId}/generateOTP`);
      toast.success('OTP code sent successfully to your email');
    } catch (error) {
      toast.error('Failed to send OTP code');
    }
  };

  const handleTaskClick = (task) => {
    navigate(`/tasks/${task._id}`);
  };

  return (
    <div className="space-y-3">
      <div className="mb-4 flex justify-between">
        <div className="flex space-x-4">
          <input
            type="text"
            placeholder="Search tasks"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
          />
          
          <select 
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            onChange={e => setFilters(prev => ({ ...prev, status: e.target.value }))}
          >
            <option value="">Select Status</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            onChange={e => setFilters(prev => ({ ...prev, client: e.target.value }))}
          >
            <option value="">Select Client</option>
            {clients.map(client => (
              <option key={client._id} value={client._id}>{client.clientName}</option>
            ))}
          </select>

          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            onChange={e => setFilters(prev => ({ ...prev, assignedTo: e.target.value }))}
          >
            <option value="">Assigned To</option>
            {users.map(user => (
              <option key={user._id} value={user._id}>{user.firstName} {user.lastName}</option>
            ))}
          </select>

          <input
            type="date"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            onChange={e => setFilters(prev => ({ ...prev, dateRange: [e.target.value] }))}
          />
        </div>

        <button
          className="px-4 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={() => {
            setSelectedTask(null);
            setIsModalVisible(true);
          }}
        >
          Create Task
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned By</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deadline</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {tasks.map(task => (
              <tr key={task._id}>
                <td className="px-6 py-4">
                  <button 
                    className="text-blue-500 hover:text-blue-700"
                    onClick={() => handleTaskClick(task)}
                  >
                    {task.name}
                  </button>
                </td>
                <td className="px-6 py-4">{task.client?.clientName}</td>
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    {/* Assigned Users */}
                    {task.assignedTo && task.assignedTo.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {task.assignedTo.map(user => (
                          <span key={user._id} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                            {user.firstName} {user.lastName}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Assigned Groups */}
                    {task.assignedGroups && task.assignedGroups.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {task.assignedGroups.map(group => (
                          <span key={group._id} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                            {group.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {(!task.assignedTo || task.assignedTo.length === 0) && 
                     (!task.assignedGroups || task.assignedGroups.length === 0) && (
                      <span className="text-gray-500 text-sm">No assignments</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  {task.createdBy ? 
                    `${task.createdBy.firstName || ''} ${task.createdBy.lastName || ''}`.trim() || 
                    'Unknown' 
                    : 'Unknown'}
                </td>
                <td className="px-6 py-4">{new Date(task.deadline).toLocaleDateString()}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded text-sm ${
                    task.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    task.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    task.status === 'completed' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {task.status.replace('_', ' ').toUpperCase()}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex space-x-2">
                    <button
                      className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                      onClick={() => {
                        setSelectedTask(task);
                        setIsModalVisible(true);
                      }}
                    >
                      Edit
                    </button>
                    <select
                      className="px-3 py-1 border rounded"
                      defaultValue={task.status}
                      onChange={(e) => handleStatusChange(task._id, e.target.value)}
                    >
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {loading && (
        <div className="flex justify-center items-center mt-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      )}

      <TaskForm
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedTask(null);
        }}
        onSuccess={() => {
          setIsModalVisible(false);
          setSelectedTask(null);
          fetchTasks();
        }}
        task={selectedTask}
        clients={clients}
        users={users}
        groups={groups}
      />

      {isVerificationModalVisible && selectedTask && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96">
            <h2 className="text-xl font-bold mb-4">Task Verification</h2>
            
            {selectedTask.verificationTypes?.map(type => {
              const verificationType = verificationTypes.find(vt => vt._id === type.id);
              return verificationType ? (
                <div key={type.id} className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">{verificationType.label}</label>
                  {verificationType.type === 'otp-email' ? (
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        maxLength={6}
                        pattern="\d{6}"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                        onChange={e => {
                          const value = e.target.value;//.replace(/\D/g, '').slice(0, 6);
                          console.log(value);
                          setVerificationFields(prev => ({
                            ...prev,
                            ["otp"]: value
                          }));
                        }}
                        placeholder="Enter 6 digit OTP"
                      />
                      <button
                        className="mt-1 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        onClick={async () => {
                          sendCode(selectedTask._id);
                        }}
                      >
                        Resend
                      </button>
                    </div>
                  ) : verificationType.type === 'file' ? (
                    <PasteUploadArea
                      onFileSelect={async (file) => {
                        console.log(file);
                        if (file) {
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const response = await api.post(`/tasks/${selectedTask._id}/verification-file`, formData, {
                              headers: { 'Content-Type': 'multipart/form-data' },
                            });

                            // Create verification record for the uploaded verification file
                            try {
                              const verificationData = {
                                documentType: 'taskVerification',
                                documentId: selectedTask._id,
                                documentField: verificationType.label,
                                fileUrl: response.data.url,
                                fileName: file.name,
                                fileSize: file.size
                              };

                              const verificationResult = await createVerificationRecord(verificationData);
                              
                              if (verificationResult.verificationRequired) {
                                toast.info('File uploaded successfully. Verification required before approval.');
                              } else {
                                toast.success('File uploaded successfully');
                              }
                            } catch (verificationError) {
                              console.error('Verification error:', verificationError);
                              toast.success('File uploaded successfully');
                            }

                            setVerificationFields(prev => ({
                              ...prev,
                              [verificationType.label]: response.data.url
                            }));
                          } catch (error) {
                            toast.error('Failed to upload file');
                          }
                        }
                      }}
                      accept="application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      maxSize={10 * 1024 * 1024}
                      className="mt-1"
                      dragDropText="Upload a file"
                      pasteText="or paste from clipboard (Ctrl+V)"
                    >
                      <DocumentIcon className="mx-auto h-8 w-8 text-gray-400" />
                      <p className="text-sm text-gray-600">Upload a file</p>
                      <p className="text-xs text-gray-500">PDF, DOC, DOCX, JPG, PNG up to 10MB</p>
                    </PasteUploadArea>
                  ) : (
                    <input
                      type={verificationType.type || 'text'} 
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
                      value={verificationFields[verificationType.label] || ''}
                      onChange={e => setVerificationFields(prev => ({
                        ...prev,
                        [verificationType.label]: e.target.value
                      }))}
                    />
                  )}
                </div>
              ) : null;
            })}

            <div className="flex justify-end space-x-2 mt-4">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                onClick={() => {
                  setIsVerificationModalVisible(false);
                  setPendingStatusChange(null);
                  setSelectedTask(null);
                  setVerificationFields({});
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={handleVerificationSubmit}
              >
                Verify & Complete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskList;